/**
 * One mock question record for the bank, the assessment paper, and import.
 *
 * Library rows, the case-study paper, and duplicate hits in import all read
 * this list. Status, AI Spotted, stem, and stats stay the same wherever the
 * question shows up.
 */

import { toQuestionStatus } from "@/lib/question-status"
import { aiSpottedForUseCase, type LibraryAiSpotted } from "@/lib/library-ai-spotted"
import type {
  LibraryBloomLevel,
  LibraryItemType,
  LibraryLevel,
} from "@/lib/mock/library"
import { QUESTION_TYPE_LABEL } from "@/lib/question-type"

export type QuestionCatalogRecord = {
  id: string
  questionId: string
  title: string
  stem: string
  type: LibraryItemType
  difficulty: LibraryLevel | ""
  bloomLevel?: LibraryBloomLevel
  /** Draft or Published. See `lib/question-status`. */
  status: "Published" | "Draft"
  pbi?: number
  usageCount: number
  sectionId: "A" | "B" | "C"
  points: number
  /** Blueprint topic on the assessment. The bank files the same stem under Biochemistry. */
  paperTopic: string
  aiSpotted: LibraryAiSpotted
  /** Shown from the Looks good info control after a writing fix. */
  fixedSummary?: string
  options?: { text: string; isCorrect?: boolean }[]
  /**
   * Explicit exam categories. `null` means this question has none, so the
   * bank must not invent one from the topic folder.
   */
  categoryIds?: string[] | null
}

type CatalogSeed = Omit<QuestionCatalogRecord, "questionId" | "aiSpotted" | "usageCount" | "status"> & {
  status?: "Published" | "Draft"
  usageCount?: number
}

const SEEDS: CatalogSeed[] = [
  { id: "q1", title: "Beta oxidation location", stem: "Beta oxidation of fatty acids occurs in:", type: "multiple_choice", difficulty: "medium", bloomLevel: "Remember", pbi: 0.42, sectionId: "C", points: 5, paperTopic: "Diagnostic Reasoning" },
  { id: "q2", title: "Protein amino acid charge", stem: "Which amino acid substitution increases the net negative charge of a protein at physiological pH?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Understand", pbi: 0.48, usageCount: 3, sectionId: "B", points: 5, paperTopic: "Pharmacology", options: [
    { text: "Lysine replaced by glutamate", isCorrect: true },
    { text: "Glutamate replaced by lysine", isCorrect: false },
    { text: "Alanine replaced by glycine", isCorrect: false },
    { text: "Aspartate replaced by asparagine", isCorrect: false },
  ] },
  { id: "q3", title: "Citric acid cycle location", stem: "Citric acid cycle takes place in:", type: "multiple_choice", difficulty: "medium", bloomLevel: "Remember", pbi: 0.22, sectionId: "C", points: 5, paperTopic: "Pathology" },
  { id: "q4", title: "Peroxisomal oxidation", stem: "Very long chain fatty acids undergo beta oxidation in which organelle?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Remember", pbi: 0.4, sectionId: "A", points: 5, paperTopic: "Physiology", categoryIds: null },
  { id: "q5", title: "Passage and the heartbeat", stem: "Read the following passage and identify the structure that sets the heartbeat.", type: "multiple_choice", difficulty: "medium", bloomLevel: "Understand", pbi: 0.36, sectionId: "A", points: 5, paperTopic: "Cardiology" },
  { id: "q6", title: "Epinephrine receptors", stem: "Select all that apply for receptors activated by epinephrine.", type: "multiple_choice", difficulty: "medium", bloomLevel: "Apply", pbi: 0.31, sectionId: "B", points: 5, paperTopic: "Pharmacology" },
  { id: "q7", title: "Red cell buffer system", stem: "The principal intracellular buffer in erythrocytes is an", type: "multiple_choice", difficulty: "hard", bloomLevel: "Remember", pbi: 0.27, sectionId: "A", points: 5, paperTopic: "Physiology" },
  { id: "q8", title: "Amino acid charge", stem: "Which change increases the net negative charge of a soluble protein at pH 7.4?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Apply", pbi: 0.33, sectionId: "B", points: 5, paperTopic: "Pharmacology" },
  { id: "q9", title: "Triglyceride lipase", stem: "Which enzyme is not involved in the release of fatty acids from stored triglyceride?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Understand", pbi: 0.29, sectionId: "C", points: 5, paperTopic: "Diagnostic Reasoning" },
  { id: "q10", title: "Facial motor innervation", stem: "Muscles of facial expression are innervated by cranial nerve ____.", type: "fill_blank", difficulty: "easy", bloomLevel: "Remember", pbi: 0.44, sectionId: "A", points: 5, paperTopic: "Cardiology", categoryIds: null },
  { id: "q11", title: "Disease state matching", stem: "Match the disease states with the precaution they require.", type: "matching", difficulty: "medium", bloomLevel: "Understand", pbi: 0.3, sectionId: "C", points: 5, paperTopic: "Pathology" },
  { id: "q12", title: "Heart chamber for deoxygenated blood", stem: "Which chamber of the heart receives deoxygenated blood from the systemic veins?", type: "multiple_choice", difficulty: "easy", bloomLevel: "Remember", pbi: 0.22, sectionId: "C", points: 5, paperTopic: "Diagnostic Reasoning" },
  { id: "q13", title: "Enzyme properties", stem: "All of the following are true for enzymes, EXCEPT", type: "multiple_choice", difficulty: "medium", bloomLevel: "Understand", pbi: 0.12, sectionId: "B", points: 5, paperTopic: "Pharmacology" },
  { id: "q14", title: "ATP synthesis site", stem: "Which site is the primary site of ATP synthesis in an aerobic cell?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Remember", pbi: 0.46, sectionId: "B", points: 5, paperTopic: "Pharmacology", categoryIds: null },
  { id: "q15", title: "Warfarin target", stem: "Which of the following statements about Warfarin is correct?", type: "true_false", difficulty: "easy", bloomLevel: "Remember", pbi: 0.18, sectionId: "B", points: 5, paperTopic: "Pharmacology" },
  { id: "q16", title: "HFpEF pathophysiology", stem: "A 68-year-old with heart failure with preserved ejection fraction asks why symptoms worsen when filling pressure rises. Explain the mechanism, one consequence, and when to seek care.", type: "essay", difficulty: "hard", bloomLevel: "Analyze", pbi: 0.41, sectionId: "C", points: 5, paperTopic: "Patient Communication" },
  { id: "q17", title: "", stem: "Describe what is required for this process to proceed in a healthy adult.", type: "essay", difficulty: "", usageCount: 0, sectionId: "C", points: 10, paperTopic: "Diagnostic Reasoning", categoryIds: null },
  { id: "q18", title: "Pacemaker inward current", stem: "Using the attached passage, which ion carries most of the inward current of the cardiac pacemaker potential?", type: "multiple_choice", difficulty: "medium", bloomLevel: "Understand", pbi: 0.39, sectionId: "C", points: 5, paperTopic: "Diagnostic Reasoning", categoryIds: null },
  { id: "q19", title: "Ketone bodies and blood pH", stem: "How do rising ketone bodies change blood pH in a fasting adult?", type: "multiple_choice", difficulty: "easy", bloomLevel: "Apply", pbi: 0.2, usageCount: 2, sectionId: "B", points: 5, paperTopic: "Pharmacology" },
  { id: "q20", title: "Tumor macrophages", stem: "Explain how tumor-associated macrophages shape antigen presentation in the tumor microenvironment.", type: "essay", difficulty: "hard", bloomLevel: "Analyze", pbi: 0.43, usageCount: 5, sectionId: "A", points: 5, paperTopic: "Physiology" },
  { id: "q17-draft", title: "", stem: "Describe.", type: "essay", difficulty: "medium", status: "Draft", sectionId: "C", points: 10, paperTopic: "Diagnostic Reasoning", categoryIds: null },
  {
    id: "q21",
    title: "Lisinopril starting dose",
    stem: "What is the recommended initial daily oral dosage of lisinopril for an adult with uncomplicated stage 1 essential hypertension?",
    type: "multiple_choice",
    difficulty: "medium",
    bloomLevel: "Apply",
    pbi: 0.34,
    sectionId: "B",
    points: 5,
    paperTopic: "Pharmacology",
    options: [
      { text: "10 mg", isCorrect: true },
      { text: "10 to 20 mg", isCorrect: false },
      { text: "40 mg", isCorrect: false },
      { text: "80 mg", isCorrect: false },
    ],
  },
  {
    id: "q22",
    title: "Cholesterol rate limiting enzyme",
    stem: "Which enzyme catalyzes the rate-limiting step in de novo cholesterol biosynthesis?",
    type: "multiple_choice",
    difficulty: "medium",
    bloomLevel: "Remember",
    pbi: 0.38,
    sectionId: "A",
    points: 5,
    paperTopic: "Physiology",
    options: [
      { text: "HMG-CoA reductase", isCorrect: true },
      { text: "High-fat dietary intake", isCorrect: false },
      { text: "Located in the liver cytosol", isCorrect: false },
      { text: "Statin medication therapy", isCorrect: false },
    ],
  },
  {
    id: "q23",
    title: "Pediatric shock",
    stem: "Shock in pediatric patients.",
    type: "multiple_choice",
    difficulty: "hard",
    bloomLevel: "Apply",
    pbi: 0.26,
    sectionId: "C",
    points: 5,
    paperTopic: "Diagnostic Reasoning",
    options: [
      { text: "Hypovolemic", isCorrect: false },
      { text: "20 mL/kg normal saline bolus", isCorrect: true },
      { text: "Bradycardia is a late finding", isCorrect: false },
      { text: "Septic shock", isCorrect: false },
    ],
  },
  {
    id: "q24",
    title: "Missing asthma case",
    stem: "Which first-line treatment is indicated for the patient in the case below?",
    type: "multiple_choice",
    difficulty: "medium",
    bloomLevel: "Apply",
    pbi: 0.33,
    sectionId: "B",
    points: 5,
    paperTopic: "Pharmacology",
    options: [
      { text: "Low-dose inhaled corticosteroid", isCorrect: true },
      { text: "Oral montelukast", isCorrect: false },
      { text: "Propranolol", isCorrect: false },
      { text: "As-needed albuterol", isCorrect: false },
    ],
  },
  {
    id: "q25",
    title: "ABG panel lead-in",
    stem: "Using the attached panel, the disturbance is:",
    type: "multiple_choice",
    difficulty: "medium",
    bloomLevel: "Analyze",
    pbi: 0.29,
    sectionId: "A",
    points: 5,
    paperTopic: "Cardiology",
    options: [
      { text: "Acute respiratory acidosis", isCorrect: true },
      { text: "Respiratory acidosis with a PaCO2 of 64", isCorrect: false },
      { text: "Normal acid-base balance", isCorrect: false },
      { text: "Metabolic alkalosis", isCorrect: false },
    ],
  },
  {
    id: "q26",
    title: "HFrEF rate control",
    stem: "Which medication is the recommended first-line therapy for rate control in atrial fibrillation with rapid ventricular response in a patient with heart failure with reduced ejection fraction (HFrEF)?",
    type: "multiple_choice",
    difficulty: "hard",
    bloomLevel: "Apply",
    pbi: -0.08,
    sectionId: "B",
    points: 5,
    paperTopic: "Pharmacology",
    options: [
      { text: "Diltiazem", isCorrect: true },
      { text: "Metoprolol succinate", isCorrect: false },
      { text: "Verapamil", isCorrect: false },
      { text: "Digoxin", isCorrect: false },
    ],
  },
  {
    id: "q27",
    title: "Systemic pump chamber",
    stem: "Which chamber of the heart pumps oxygenated blood into the aorta?",
    type: "multiple_choice",
    difficulty: "easy",
    bloomLevel: "Remember",
    pbi: 0.04,
    usageCount: 4,
    sectionId: "A",
    points: 5,
    paperTopic: "Physiology",
    options: [
      { text: "Left ventricle", isCorrect: true },
      { text: "Right ventricle", isCorrect: false },
      { text: "Left atrium", isCorrect: false },
      { text: "Right atrium", isCorrect: false },
    ],
  },
]

export const QUESTION_CATALOG: QuestionCatalogRecord[] = SEEDS.map((row, index) => ({
  ...row,
  questionId: `CASE-2026-${String(index + 1).padStart(3, "0")}`,
  status: row.status ?? "Published",
  usageCount: row.usageCount ?? 1,
  aiSpotted: aiSpottedForUseCase(row.id),
}))

const BY_ID = new Map(QUESTION_CATALOG.map((row) => [row.id, row]))
const BY_QUESTION_ID = new Map(QUESTION_CATALOG.map((row) => [row.questionId, row]))

export function questionCatalogById(id: string | undefined): QuestionCatalogRecord | undefined {
  if (!id) return undefined
  return BY_ID.get(id) ?? BY_QUESTION_ID.get(id) ?? BY_ID.get(id.replace(/^ai-case-/, ""))
}

/** The id printed in the bank, the sheet, and the editor. */
export function publicQuestionCode(id: string | undefined): string {
  if (!id) return ""
  return questionCatalogById(id)?.questionId ?? id
}

/** The paper prints the same spelling as the bank and the library. */
const PAPER_TYPE = QUESTION_TYPE_LABEL

const PAPER_BLOOM: Record<LibraryBloomLevel, string> = {
  Remember: "Remembering",
  Understand: "Understanding",
  Apply: "Applying",
  Analyze: "Analyzing",
  Evaluate: "Evaluating",
  Create: "Creating",
}

const PAPER_DIFFICULTY: Record<LibraryLevel, "Easy" | "Medium" | "Hard"> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
}

/** Assessment roster row for one catalog question. */
export function catalogPaperQuestion(row: QuestionCatalogRecord, number: number) {
  const difficulty = row.difficulty ? PAPER_DIFFICULTY[row.difficulty] : ""
  const uses = row.usageCount
  return {
    id: row.id,
    number,
    stem: row.stem,
    title: row.title || undefined,
    type: PAPER_TYPE[row.type],
    topic: row.paperTopic,
    difficulty,
    difficultyPercentage: row.pbi != null ? Math.round(row.pbi * 100) : 0,
    pointBiserial: row.pbi ?? 0,
    discriminationIndex: row.pbi ?? 0,
    blooms: row.bloomLevel ? PAPER_BLOOM[row.bloomLevel] : "",
    points: row.points,
    usedBefore: uses > 0,
    usedCount: uses,
    usedInSameCourse: uses >= 3,
    sectionId: row.sectionId,
    status: toQuestionStatus(row.status),
    aiSpotted: row.aiSpotted,
    fixedSummary: row.fixedSummary,
    questionId: row.questionId,
  }
}

const BANK_DIFFICULTY: Record<LibraryLevel, "Easy" | "Medium" | "Hard"> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
}

/**
 * One catalog question as a row of the Add questions pool.
 *
 * **Why this exists.** The pool was built only from
 * `ASSESSMENT_BANK_QUESTIONS`, so all 28 catalog questions — every
 * CASE-2026-xxx the library lists and the assessment paper is built from —
 * were simply absent from Add questions. Not described differently:
 * missing. An author could read a question in the bank, open Add questions
 * to put it on a paper, and not find it.
 *
 * The row keeps the catalog `id`, which is what makes the rest line up:
 * `readLeoSpotted`, the session overlay and `questionCatalogById` are all
 * keyed off it, so the pool row, the bank row and the roster row are three
 * views of one question rather than three questions.
 */
export function catalogBankQuestion(row: QuestionCatalogRecord) {
  return {
    id: row.id,
    questionId: row.questionId,
    title: row.title || row.stem.slice(0, 60),
    stem: row.stem,
    type: PAPER_TYPE[row.type],
    topic: row.paperTopic,
    difficulty: row.difficulty ? BANK_DIFFICULTY[row.difficulty] : ("Medium" as const),
    difficultyTagged: Boolean(row.difficulty),
    sourceKind: "library" as const,
    sourceLabel: "Laboratory Interpretation",
    folderId: "laboratory-interpretation",
    // `--` is the pool's "never sat". A catalog question without a PBI has
    // not been measured, which is not the same as scoring zero.
    pbi: row.pbi != null ? String(row.pbi) : "--",
    points: row.points,
    bloom: row.bloomLevel,
    status: toQuestionStatus(row.status),
    timesUsed: row.usageCount,
    neverUsed: row.usageCount === 0,
    ...(row.options?.length ? { carriedChoices: row.options } : {}),
    ...(row.categoryIds === null ? { categoryIds: [] as string[] } : {}),
  }
}

/** Every catalog question, as pool rows. */
export const CATALOG_BANK_QUESTIONS = QUESTION_CATALOG.map(catalogBankQuestion)
